import { Stack } from 'expo-router';
import { useEffect } from 'react';
import { Animated, Platform } from 'react-native';
import * as Notifications from 'expo-notifications';
import AsyncStorage from '@react-native-async-storage/async-storage';
import i18n from '../i18n';
import { getSavedLanguage } from '../services/storage';

// ══════════════════════════════════════════════════════════════════════════════
// CONFIG
// ══════════════════════════════════════════════════════════════════════════════
const WS_BASE = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com';
const API_BASE = `${WS_BASE}/auth`;

// Présence : un signal est envoyé à /revue au premier appel backend, puis
// seulement si 60 s ou plus se sont écoulées depuis le signal précédent.
const PRESENCE_URL = `${WS_BASE}/revue`;
const PRESENCE_INTERVAL_MS = 60_000;

// Routes qui ne doivent JAMAIS déclencher de signal de présence :
// /revue (évite la boucle), /logout (ne pas se remettre en ligne en
// se déconnectant), /auth et /refresh-token (pas de session valide).
const PRESENCE_EXCLUDED_PATHS = ['/revue', '/logout', '/auth', '/refresh-token'];

// ══════════════════════════════════════════════════════════════════════════════
// HELPERS
// ══════════════════════════════════════════════════════════════════════════════
const isBackendRequest = (urlStr: string) => {
  return urlStr.startsWith(WS_BASE) || urlStr.startsWith(API_BASE);
};

const isGoogleAuthRequest = (urlStr: string) => {
  return urlStr.includes('accounts.google.com');
};

const isPresenceExcluded = (urlStr: string) => {
  return PRESENCE_EXCLUDED_PATHS.some((path) =>
    urlStr.startsWith(`${WS_BASE}${path}`)
  );
};

const getAuthPayloadFromSession = async () => {
  try {
    const raw = await AsyncStorage.getItem('harmonia_session');
    if (!raw) return null;

    const session = JSON.parse(raw);

    const user_id =
      session?.user?.id ||
      session?.user_id ||
      session?.id ||
      null;

    const access_token = session?.access_token || null;

    if (!user_id || !access_token) return null;

    return { user_id, access_token };
  } catch {
    return null;
  }
};

const mergeJsonBodyWithAuth = async (init: any) => {
  const auth = await getAuthPayloadFromSession();
  if (!auth) return init;

  const nextInit = { ...init };
  const method = String(nextInit.method || 'GET').toUpperCase();

  if (method === 'GET' || method === 'HEAD') {
    return nextInit;
  }

  let existingBody: any = {};

  try {
    if (typeof nextInit.body === 'string' && nextInit.body.trim()) {
      existingBody = JSON.parse(nextInit.body);
    } else if (
      nextInit.body &&
      typeof nextInit.body === 'object' &&
      !(nextInit.body instanceof FormData) &&
      !(nextInit.body instanceof Blob) &&
      !(nextInit.body instanceof ArrayBuffer)
    ) {
      existingBody = nextInit.body;
    }
  } catch {
    existingBody = {};
  }

  const mergedBody = {
    ...existingBody,
    user_id: auth.user_id,
    access_token: auth.access_token,
  };

  nextInit.body = JSON.stringify(mergedBody);

  const headers = new Headers(nextInit.headers || {});
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  nextInit.headers = headers;

  return nextInit;
};

// ══════════════════════════════════════════════════════════════════════════════
// SIGNAL DE PRÉSENCE
// Décompte en mémoire (cache) : repart de zéro à chaque lancement de l'app,
// donc la première requête backend après le lancement envoie toujours un signal.
// ══════════════════════════════════════════════════════════════════════════════
let lastPresenceSignalAt = 0;

const sendPresenceSignal = async (rawFetch: typeof fetch): Promise<void> => {
  const now = Date.now();

  // Moins de 60 s depuis le dernier signal → rien à envoyer
  if (now - lastPresenceSignalAt < PRESENCE_INTERVAL_MS) return;

  // L'heure est notée AVANT tout await : deux requêtes simultanées
  // ne peuvent pas envoyer deux signaux.
  const previous = lastPresenceSignalAt;
  lastPresenceSignalAt = now;

  try {
    const auth = await getAuthPayloadFromSession();
    if (!auth) {
      lastPresenceSignalAt = previous;
      return;
    }

    // rawFetch (fetch d'origine) : ce signal ne repasse pas par le patch
    const response = await rawFetch(PRESENCE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth.access_token}`,
      },
      body: JSON.stringify({
        action: 'heartbeat',
        user_id: auth.user_id,
        access_token: auth.access_token,
      }),
    });

    // Échec (token expiré, serveur indisponible...) → on réessaiera
    // à la prochaine requête au lieu d'attendre 60 s
    if (!response.ok) {
      lastPresenceSignalAt = previous;
    }
  } catch {
    lastPresenceSignalAt = previous;
  }
};

// ══════════════════════════════════════════════════════════════════════════════
// PATCH FETCH GLOBAL — iOS · Android · Web
// Injecte user_id + access_token dans le body JSON des requêtes backend
// + déclenche le signal de présence (limité à 1 par 60 s)
// ══════════════════════════════════════════════════════════════════════════════
(() => {
  const originalFetch = global.fetch;

  global.fetch = async (input: any, init: any = {}) => {
    const urlStr =
      typeof input === 'string'
        ? input
        : input instanceof Request
          ? input.url
          : '';

    if (isGoogleAuthRequest(urlStr)) {
      return originalFetch(input, init);
    }

    if (isBackendRequest(urlStr)) {
      // Signal de présence en parallèle : ne retarde jamais la requête
      // de l'utilisateur, et un échec reste silencieux.
      if (!isPresenceExcluded(urlStr)) {
        sendPresenceSignal(originalFetch).catch(() => {});
      }

      const patchedInit = await mergeJsonBodyWithAuth(init);
      return originalFetch(input, patchedInit);
    }

    return originalFetch(input, init);
  };
})();

// ══════════════════════════════════════════════════════════════════════════════
// PATCH ANIMATED — Web uniquement
// ══════════════════════════════════════════════════════════════════════════════
if (Platform.OS === 'web') {
  const patch = (config: any) =>
    config && typeof config === 'object'
      ? { ...config, useNativeDriver: false }
      : config;

  const _timing = Animated.timing;
  (Animated as any).timing = (v: any, c: any) => _timing(v, patch(c));

  const _spring = Animated.spring;
  (Animated as any).spring = (v: any, c: any) => _spring(v, patch(c));

  const _decay = Animated.decay;
  (Animated as any).decay = (v: any, c: any) => _decay(v, patch(c));
}
// ══════════════════════════════════════════════════════════════════════════════

export default function RootLayout() {
  useEffect(() => {
    Notifications.setNotificationHandler({
      handleNotification: async () => ({
        shouldShowAlert: true,
        shouldPlaySound: true,
        shouldSetBadge: false,
      }),
    });

    (async () => {
      const savedLanguage = await getSavedLanguage();
      if (savedLanguage && savedLanguage !== i18n.language) {
        i18n.changeLanguage(savedLanguage);
      }
    })();
  }, []);

  return <Stack screenOptions={{ headerShown: false }} />;
        }
